import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  getCloseModalHistoryAction,
  getModalHash,
  getOwnerId,
  getStateFromActiveModal,
  getUrlWithModalHash,
  getUrlWithoutModalHash,
  isMatchingModal,
  shouldClearUnsupportedModalHash,
  type ActiveHashModal,
  type HashModalOptions,
  type HashModalState,
} from "@/hooks/hash-modal-state";

interface HashModalContextValue {
  activeModal: string | null;
  activeOwnerId: string | null;
  close: (modal?: string, options?: HashModalOptions) => void;
  getPayload: <TPayload>(modal: string, options?: HashModalOptions) => TPayload | null;
  getState: <TPayload>(modal: string, options?: HashModalOptions) => HashModalState<TPayload>;
  isOpen: (modal: string, options?: HashModalOptions) => boolean;
  open: <TPayload = true>(modal: string, payload?: TPayload, options?: HashModalOptions) => void;
}

const HashModalContext = createContext<HashModalContextValue | null>(null);

function getCurrentModalHash() {
  return getModalHash(window.location.hash);
}

function getCurrentLocationParts() {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

function getCurrentUrlWithModalHash(modal: string) {
  return getUrlWithModalHash(getCurrentLocationParts(), modal);
}

function getCurrentUrlWithoutModalHash() {
  return getUrlWithoutModalHash(getCurrentLocationParts());
}

function pushModalHash(modal: string) {
  if (getCurrentModalHash() === modal) return false;
  History.prototype.pushState.call(
    window.history,
    window.history.state,
    "",
    getCurrentUrlWithModalHash(modal),
  );
  return true;
}

function removeModalHash(pushed: boolean) {
  if (getCloseModalHistoryAction({ pushed, historyLength: window.history.length }) === "back") {
    window.history.back();
    return;
  }

  replaceModalHash();
}

function replaceModalHash() {
  History.prototype.replaceState.call(
    window.history,
    window.history.state,
    "",
    getCurrentUrlWithoutModalHash(),
  );
}

export function HashModalProvider({ children }: { children: ReactNode }) {
  const [activeModal, setActiveModalState] = useState<ActiveHashModal | null>(null);
  const activeModalRef = useRef<ActiveHashModal | null>(null);

  const setActiveModal = useCallback((next: ActiveHashModal | null) => {
    activeModalRef.current = next;
    setActiveModalState(next);
  }, []);

  const open = useCallback(
    <TPayload>(modal: string, payload: TPayload = true as TPayload, options?: HashModalOptions) => {
      const current = activeModalRef.current;
      const ownerId = getOwnerId(options);
      const pushed =
        current?.modal === modal && getCurrentModalHash() === modal
          ? current.pushed
          : pushModalHash(modal);

      setActiveModal({ modal, ownerId, payload, pushed });
    },
    [setActiveModal],
  );

  const close = useCallback(
    (modal?: string, options?: HashModalOptions) => {
      const current = activeModalRef.current;
      if (!current || (modal && !isMatchingModal(current, modal, options))) return;

      setActiveModal(null);
      if (getCurrentModalHash() === current.modal) {
        removeModalHash(current.pushed);
      }
    },
    [setActiveModal],
  );

  useEffect(() => {
    const clearUnsupportedDirectHash = () => {
      const current = activeModalRef.current;
      const modalHash = getCurrentModalHash();
      if (shouldClearUnsupportedModalHash(current, modalHash)) {
        replaceModalHash();
      }
    };

    clearUnsupportedDirectHash();

    const reconcileLocation = () => {
      const current = activeModalRef.current;
      const modalHash = getCurrentModalHash();
      if (!current) {
        if (modalHash) replaceModalHash();
        return;
      }
      if (modalHash === current.modal) return;
      setActiveModal(null);
      if (modalHash) replaceModalHash();
    };

    window.addEventListener("hashchange", reconcileLocation);
    window.addEventListener("popstate", reconcileLocation);
    return () => {
      window.removeEventListener("hashchange", reconcileLocation);
      window.removeEventListener("popstate", reconcileLocation);
    };
  }, [setActiveModal]);

  const isOpen = useCallback(
    (modal: string, options?: HashModalOptions) => isMatchingModal(activeModal, modal, options),
    [activeModal],
  );

  const getState = useCallback(
    <TPayload>(modal: string, options?: HashModalOptions): HashModalState<TPayload> => {
      return getStateFromActiveModal<TPayload>(activeModal, modal, options);
    },
    [activeModal],
  );

  const getPayload = useCallback(
    <TPayload>(modal: string, options?: HashModalOptions) =>
      getState<TPayload>(modal, options).payload,
    [getState],
  );

  const value = useMemo<HashModalContextValue>(
    () => ({
      activeModal: activeModal?.modal ?? null,
      activeOwnerId: activeModal?.ownerId ?? null,
      close,
      getPayload,
      getState,
      isOpen,
      open,
    }),
    [activeModal?.modal, activeModal?.ownerId, close, getPayload, getState, isOpen, open],
  );

  return createElement(HashModalContext.Provider, { value }, children);
}

export function useHashModal() {
  const context = useContext(HashModalContext);
  if (!context) throw new Error("useHashModal must be used within <HashModalProvider>");
  return context;
}
